import { useEffect, useMemo, useState } from "react";
import { Plus, Minus, Search, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useAddAsset,
  useMyWalletAndAssets,
  useToggleAsset,
  useTokens,
} from "@/hooks";
import { useAppSelector } from "@/store/hooks";
import {
  formatCurrency,
  normalizeCurrency,
  removeTrailingToken,
} from "@/utils";
import Loader from "@/components/loader/Loader";
import { ErrorState } from "@/components/ErrorState";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import type { Token } from "@/interfaces/token";
import { LedgerAccountType, type WalletAsset } from "@/interfaces/asset";

export default function P2PAssetsPage() {
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id as string;

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const navigate = useNavigate();

  const addAssetMutation = useAddAsset(userId);

  const toggleAssetMutation = useToggleAsset(userId);

  const {
    data: myWallet,
    isLoading,
    isError,
    refetch,
  } = useMyWalletAndAssets(userId);

  const {
    data: tokenData,
    isLoading: isLoadingTokens,
    isError: isErrorTokens,
    refetch: refetchTokens,
  } = useTokens(1, 30, debouncedSearch);

  console.log("TOKENS", tokenData);

  console.log("START---------------");

  console.table(
    myWallet?.assets?.map((a) => ({
      token: a.token.symbol,
      available: a.available,
      number: Number(a.available),
    })),
  );

  console.log("END---------------");

  const activeAssets: WalletAsset[] = [...(myWallet?.assets ?? [])].sort(
    (a, b) => {
      const balanceDiff = Number(b.available ?? 0) - Number(a.available ?? 0);

      if (balanceDiff !== 0) return balanceDiff;

      return (a.token?.name ?? "").localeCompare(b.token?.name ?? "");
    },
  );

  const tokenAssets: Token[] = tokenData?.data || [];

  console.log("ACTIVE", activeAssets);

  const activeTokenIds = new Set(activeAssets.map((a) => a.tokenId));

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const filteredInactive = useMemo(() => {
    return tokenAssets.filter((token) => !activeTokenIds.has(token.id));
  }, [tokenAssets, activeAssets]);

  const handleAssetAction = (id: string, shouldEnable: boolean) => {
    if (shouldEnable) {
      addAssetMutation.mutate({
        tokenId: id,
      });
      return;
    }

    toggleAssetMutation.mutate(id);
  };

  if (isLoading) {
    return <Loader type="bounce" />;
  }

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <Button
        className="hidden sm:flex"
        variant="ghost"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      <div className="sticky top-16 lg:top-20 z-30 bg-background/80 backdrop-blur border-b pb-4 mb-12">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold">Asset Control</h1>
            <p className="text-xs text-muted-foreground">
              Enable or disable assets/currencies
            </p>
          </div>

          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search new assets/currencies to add..."
              className={cn(
                "w-full pl-10 pr-4 py-2.5 text-sm",
                "rounded-full border border-border",
                "bg-card shadow-sm",
                "focus:outline-none focus:ring-2 focus:ring-primary/30",
              )}
            />
          </div>
        </div>
      </div>

      <section className="space-y-3">
        <h2 className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
          Active Currencies ({activeAssets.length})
        </h2>

        <div className="rounded-xl border bg-card overflow-hidden">
          {activeAssets.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">
              No active trading assets
            </div>
          ) : (
            activeAssets.map((asset, idx) => {
              const available = asset?.accounts?.find(
                (account) =>
                  (account.type as LedgerAccountType) ===
                  LedgerAccountType.AVAILABLE,
              );

              const mainIcon = asset.iso2
                ? `https://flagcdn.com/w80/${asset.iso2.toLowerCase()}.png`
                : asset?.token?.icon;

              const badgeIcon = asset?.token?.blockchainNetwork?.icon;

              return (
                <div
                  key={asset.id}
                  className={cn(
                    "flex items-center justify-between px-2 py-2",
                    "hover:bg-muted/40 transition",
                    idx !== activeAssets.length - 1 && "border-b",
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative h-12 w-12">
                      <img
                        src={mainIcon}
                        className="h-12 w-12 rounded-full object-cover"
                      />

                      {!asset.iso2 && (
                        <img
                          src={badgeIcon}
                          className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-background bg-background"
                        />
                      )}
                    </div>

                    <div>
                      <p className="text-sm font-medium">
                        {removeTrailingToken(asset?.token?.name)}{" "}
                        <span className="text-muted-foreground">
                          ({normalizeCurrency(asset.token.code)})
                        </span>
                      </p>

                      <p className="text-xs text-muted-foreground">
                        Balance:
                        <span className="text-xs font-bold text-emerald-500">
                          {formatCurrency(Number(available?.balance) || 0)}
                        </span>
                      </p>
                    </div>
                  </div>

                  <button
                    disabled={toggleAssetMutation.isPending}
                    onClick={() => handleAssetAction(asset.id, false)}
                    className={cn(
                      "flex items-center gap-1 px-3 py-1.5 text-xs rounded-full",
                      "bg-red-500/10 text-red-500 hover:bg-red-500/20",
                    )}
                  >
                    <Minus className="h-3.5 w-3.5" />
                    Remove
                  </button>
                </div>
              );
            })
          )}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
          Available Assets ({tokenAssets?.length})
        </h2>

        <div className="rounded-xl border bg-card overflow-hidden">
          {filteredInactive?.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">
              No available assets found
            </div>
          ) : (
            filteredInactive?.map((token, idx) => {
              const mainIcon = token.iso2
                ? `https://flagcdn.com/w80/${token.iso2.toLowerCase()}.png`
                : token?.icon;

              const badgeIcon = token?.blockchainNetwork?.icon;

              return (
                <div
                  key={token.id}
                  className={cn(
                    "flex items-center justify-between px-2 py-2",
                    "hover:bg-muted/40 transition",
                    idx !== filteredInactive.length - 1 && "border-b",
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative h-12 w-12">
                      <img
                        src={mainIcon}
                        className="h-12 w-12 rounded-full object-cover"
                      />

                      {!token.iso2 && (
                        <img
                          src={badgeIcon}
                          className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-background bg-background"
                        />
                      )}
                    </div>
                    {/* <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                    <span className="text-xs font-bold text-muted-foreground">
                      {token?.iso}
                    </span>
                  </div> */}

                    <div>
                      <p className="text-sm font-medium">
                        {removeTrailingToken(token?.name)}{" "}
                        <span className="text-muted-foreground">
                          ({token?.iso})
                        </span>
                      </p>

                      {/* <p className="text-xs text-muted-foreground">
                      Balance: {formatCurrency(Number(token.balance) || 0)}
                    </p> */}
                    </div>
                  </div>

                  <button
                    disabled={addAssetMutation.isPending}
                    onClick={() => handleAssetAction(token.id, true)}
                    className={cn(
                      "flex items-center gap-1 px-3 py-1.5 text-xs rounded-full",
                      "bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20",
                    )}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add
                  </button>
                </div>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}
